import { ReactNode } from "react";
import { RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

export type Column = {
  key: string;
  label: string;
  width: string;
};

type TableContainerProps<T> = {
  columns: Column[];
  data: T[];
  renderCell: (item: T, columnKey: string) => ReactNode;
  renderMobileCard?: (item: T) => ReactNode;
  getRowKey?: (item: T) => string;
  refetch?: () => void;
  canPrevious?: boolean;
  canNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  pagination: boolean;
};

const TableContainer = <T extends object>({
  columns,
  data,
  renderCell,
  renderMobileCard,
  getRowKey = (item: any) => item.id,
  refetch,
  canPrevious = false,
  canNext = false,
  onPrevious,
  onNext,
  pagination = false,
}: TableContainerProps<T>) => {
  const isMobile = useIsMobile();

  return (
    <div className="relative flex w-full flex-col rounded-lg border border-gray-primary shadow-[0px_4px_20px_1px_rgba(125,124,135,0.20)]">
      {/* Content Area */}
      {isMobile && renderMobileCard ? (
        <div className="flex flex-col bg-white">
          {data.map((item) => {
            const rowKey = getRowKey(item);
            return (
              <div key={rowKey} className="border-b border-gray-primary">
                {renderMobileCard(item)}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto bg-white md:overflow-x-visible">
          <div
            className="grid min-w-[800px] bg-white md:min-w-min"
            style={{ gridTemplateColumns: columns.map((col) => col.width).join(" ") }}
          >
            {/* Header */}
            {columns.map((column) => (
              <div
                key={column.key}
                className="sticky top-0 z-[1] border-b border-gray-primary bg-white p-3 font-semibold"
              >
                <span>{column.label}</span>
              </div>
            ))}

            {/* Rows */}
            {data.map((item) => {
              const rowKey = getRowKey(item);
              return columns.map((column) => (
                <div
                  key={`${rowKey}-${column.key}`}
                  className="flex w-full border-b border-gray-primary p-3"
                >
                  <div className="w-full self-center">
                    {renderCell(item, column.key)}
                  </div>
                </div>
              ));
            })}
          </div>
        </div>
      )}

      {/* Fixed Footer */}
      <div
        className={cn(
          "flex items-center gap-4 border-t border-gray-primary bg-transparent p-3",
          pagination ? "justify-between" : "justify-end"
        )}
      >
        {pagination && (
          <div className="flex items-center gap-3">
            <ChevronLeft
              className={cn(
                "size-6 rounded-full p-1.5 transition-all",
                canPrevious
                  ? "cursor-pointer bg-gray-200 text-gray-500 hover:scale-110 hover:bg-palomma-primary-dark"
                  : "cursor-not-allowed bg-gray-100 text-gray-300"
              )}
              onClick={canPrevious ? onPrevious : undefined}
            />
            <ChevronRight
              className={cn(
                "size-6 rounded-full p-1.5 transition-all",
                canNext
                  ? "cursor-pointer bg-gray-200 text-gray-500 hover:scale-110 hover:bg-palomma-primary-dark"
                  : "cursor-not-allowed bg-gray-100 text-gray-300"
              )}
              onClick={canNext ? onNext : undefined}
            />
          </div>
        )}
        {refetch && (
          <RefreshCw className="cursor-pointer text-gray-500" onClick={refetch} />
        )}
      </div>
    </div>
  );
};

export default TableContainer;
